import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { adminMe, getAdminToken, getContent, saveContent } from "./api";
import { toast } from "sonner";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  // Saved overrides loaded from server (key → value)
  const [overrides, setOverrides] = useState({});
  // Pending edits not yet saved
  const [drafts, setDrafts] = useState({});
  // Whether the viewer is the admin
  const [isAdmin, setIsAdmin] = useState(false);
  // Whether inline editing is currently active
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const draftsRef = useRef(drafts);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  // Load overrides + check admin status on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getContent();
        if (!cancelled) setOverrides(data.values || {});
      } catch {
        /* default content used */
      }
      if (getAdminToken()) {
        try {
          await adminMe();
          if (!cancelled) setIsAdmin(true);
        } catch {
          if (!cancelled) setIsAdmin(false);
        }
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const get = useCallback(
    (key, fallback = "") => {
      if (Object.prototype.hasOwnProperty.call(drafts, key)) return drafts[key];
      if (Object.prototype.hasOwnProperty.call(overrides, key)) return overrides[key];
      return fallback;
    },
    [drafts, overrides]
  );

  const setDraft = useCallback((key, value) => {
    setDrafts((prev) => {
      // Only mark dirty if different from current saved value
      const next = { ...prev };
      next[key] = value;
      return next;
    });
  }, []);

  const dirtyCount = useMemo(() => {
    let n = 0;
    for (const k of Object.keys(drafts)) {
      const current = overrides[k] ?? null;
      const draft = drafts[k];
      if ((current ?? "") !== (draft ?? "")) n += 1;
    }
    return n;
  }, [drafts, overrides]);

  const discardDrafts = useCallback(() => {
    setDrafts({});
  }, []);

  const save = useCallback(async () => {
    if (dirtyCount === 0) {
      toast.info("No changes to save.");
      return;
    }
    setSaving(true);
    try {
      const merged = { ...overrides, ...drafts };
      await saveContent(merged);
      setOverrides(merged);
      setDrafts({});
      toast.success(`Saved ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  }, [drafts, overrides, dirtyCount]);

  const enterEdit = useCallback(() => setEditMode(true), []);
  const exitEdit = useCallback(() => {
    if (dirtyCount > 0) {
      if (!window.confirm("You have unsaved edits. Exit anyway?")) return;
    }
    setDrafts({});
    setEditMode(false);
  }, [dirtyCount]);

  const value = useMemo(
    () => ({
      get,
      setDraft,
      isAdmin,
      editMode,
      enterEdit,
      exitEdit,
      save,
      discardDrafts,
      dirtyCount,
      saving,
      loaded,
    }),
    [get, setDraft, isAdmin, editMode, enterEdit, exitEdit, save, discardDrafts, dirtyCount, saving, loaded]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}

// Convenience hook to get a single value with a fallback
export function useText(key, fallback) {
  const { get } = useContent();
  return get(key, fallback);
}
